import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db, testFirestoreConnection } from '../../firebaseConfig';
import { useCart } from '../../contexts/CartContext';
import { useOrderStatus } from '../../contexts/OrderStatusContext';
import { useNavigate } from 'react-router-dom';
import QuantityModal from '../../components/QuantityModal';
import BottomCartBar from '../../components/BottomCartBar';
import OrderStatusBar from '../../components/OrderStatusBar';
import FirestoreTest from '../../components/FirestoreTest';
import { 
  Search, 
  Star, 
  Clock, 
  TrendingUp, 
  Heart, 
  Share2, 
  Bookmark,
  Menu,
  ChefHat,
  ShoppingCart,
  Grid3X3,
  Utensils,
  Coffee,
  IceCream,
  Cookie,
  Wine,
  Sparkles,
  Plus,
  X,
  User,
  History,
  MapPin,
  LogOut
} from 'lucide-react';

const HomePageNew = () => {
  console.log('🔄 HomePageNew: Component rendering');
  
  const [foodItems, setFoodItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  const { addToCart, getCartTotal, getCartItemCount } = useCart();
  const { currentOrder } = useOrderStatus();
  const navigate = useNavigate();

  // Category ordering for proper display
  const categoryOrder = ['appetizers', 'main-course', 'desserts', 'beverages'];

  const fetchFoodItems = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 Fetching food items for homepage...');
      const q = query(collection(db, 'food_items'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📦 Homepage fetched items:', items.length, 'items');

      setFoodItems(items);
      const uniqueCategories = [...new Set(items.map(item => item.category))];
      setCategories(uniqueCategories);
      setIsLoading(false);

    } catch (error) {
      console.error('Error fetching food items:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔄 HomePageNew: useEffect triggered - fetching food items');
    
    // Test Firestore connection first
    testFirestoreConnection().then(isConnected => {
      if (isConnected) {
        fetchFoodItems();
      } else {
        console.error('❌ Cannot fetch food items - Firestore connection failed');
        setIsLoading(false);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter items based on search and category
  const filteredItems = foodItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group items by category
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Sort categories according to categoryOrder
  const sortedCategories = categoryOrder.filter(cat => groupedItems[cat]?.length > 0);

  const openQuantityModal = (item) => {
    setSelectedItem(item);
    setShowQuantityModal(true);
  };

  const handleQuantityModalAddToCart = (item, quantity, portion) => {
    console.log('🛒 HomePageNew: Adding to cart with:', { item, quantity, portion });
    addToCart(item, portion, quantity);
    setShowQuantityModal(false);
  };

  const handleSidebarNavigation = (path) => {
    setShowSidebar(false);
    navigate(path);
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'appetizers': Utensils,
      'main-course': ChefHat,
      'desserts': IceCream,
      'beverages': Coffee,
      'snacks': Cookie,
      'drinks': Wine,
      'special': Sparkles
    };
    return iconMap[category] || Grid3X3;
  };

  const FoodItemCard = ({ item }) => {
    const price = typeof item.price === 'object' ? item.price.half : item.price;

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: 'rgba(0, 0, 0, 0.08) 0px 1px 4px',
          transition: 'transform 0.2s, box-shadow 0.2s',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'rgba(0, 0, 0, 0.12) 0px 4px 8px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'rgba(0, 0, 0, 0.08) 0px 1px 4px';
        }}
        onClick={() => openQuantityModal(item)}
      >
        {/* Left side - Content */}
        <div style={{ flex: '1 1 0%', minWidth: '0px' }}>
          {/* Veg/Non-Veg Badge */}
          <div style={{ marginBottom: '6px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              backgroundColor: item.isVeg ? 'rgb(220, 252, 231)' : 'rgb(254, 226, 226)',
              color: item.isVeg ? 'rgb(22, 101, 52)' : 'rgb(153, 27, 27)',
              border: `1px solid ${item.isVeg ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'}`,
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px',
              transition: '0.2s'
            }}>
              <span style={{ fontSize: '12px' }}>●</span>
              <span>{item.isVeg ? 'VEG' : 'NON-VEG'}</span>
            </div>
          </div>

          {/* Food Name */}
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'rgb(31, 41, 55)',
            margin: '0px 0px 6px',
            lineHeight: '1.3'
          }}>
            {item.name}
          </h3>

          {/* Rating and Time */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '6px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              backgroundColor: 'rgba(251, 191, 36, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(251, 191, 36, 0.2)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgb(251, 191, 36) 0%, rgb(245, 158, 11) 100%)',
                borderRadius: '50%',
                padding: '1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Star size={10} color="white" fill="white" />
              </div>
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'rgb(146, 64, 14)'
              }}>
                {item.rating}
              </span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(99, 102, 241, 0.2)'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgb(99, 102, 241) 0%, rgb(79, 70, 229) 100%)',
                borderRadius: '50%',
                padding: '1px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Clock size={10} color="white" />
              </div>
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: 'rgb(67, 56, 202)'
              }}>
                {item.prepTime}
              </span>
            </div>
          </div>

          {/* Popular Choice Badge */}
          {item.isPopular && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginBottom: '6px',
              padding: '2px 6px',
              background: 'linear-gradient(135deg, rgb(16, 185, 129) 0%, rgb(5, 150, 105) 100%)',
              borderRadius: '8px',
              boxShadow: 'rgba(16, 185, 129, 0.2) 0px 1px 2px'
            }}>
              <TrendingUp size={12} color="white" />
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'white'
              }}>
                Popular Choice
              </span>
            </div>
          )}

          {/* Price */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'rgb(31, 41, 55)',
            marginBottom: '6px'
          }}>
            ₹{price}
          </div>

          {/* Description */}
          <p style={{
            fontSize: '12px',
            color: 'rgb(107, 114, 128)',
            margin: '0px 0px 8px',
            lineHeight: '1.3',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}>
            {item.description}
          </p>

          {/* Action Icons */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <button style={{
              padding: '2px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Bookmark size={16} color="#6b7280" />
            </button>
            <button style={{
              padding: '2px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Share2 size={16} color="#6b7280" />
            </button>
          </div>
        </div>

        {/* Right side - Image and ADD button */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '6px',
          minWidth: '140px'
        }}>
          {/* Food Image */}
          <div style={{
            width: '120px',
            height: '120px',
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 8px'
          }}>
            <img
              src={item.image}
              alt={item.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>

          {/* ADD Button */}
          <button
            style={{
              backgroundColor: 'rgb(255, 107, 53)',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: '0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              width: '100%',
              justifyContent: 'center',
              boxShadow: 'rgba(255, 107, 53, 0.3) 0px 1px 3px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(234, 88, 12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgb(255, 107, 53)';
            }}
          >
            <Plus size={12} />
            ADD
          </button>

          {/* Customisable text */}
          <p style={{
            fontSize: '9px',
            color: 'rgb(156, 163, 175)',
            margin: '0px',
            textAlign: 'center'
          }}>
            customisable
          </p>
        </div>
      </motion.div>
    );
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'rgb(248, 249, 250)',
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #ff6b35',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'rgb(248, 249, 250)',
      fontFamily: 'Inter, sans-serif',
      paddingBottom: '80px'
    }}>
      {/* Firestore Connection Test */}
      <FirestoreTest />
      
      {/* Mobile Header */}
      <div style={{
        position: 'sticky',
        top: '0px',
        zIndex: 50,
        backgroundColor: 'white',
        boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 10px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        {/* Menu Button */}
        <button 
          onClick={() => setShowSidebar(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            backgroundColor: 'rgb(255, 107, 53)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          <Menu size={20} />
        </button>

        {/* Logo and Title */}
        <div style={{
          textAlign: 'center',
          flex: '1 1 0%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <div style={{
            backgroundColor: 'rgb(243, 244, 246)',
            borderRadius: '12px',
            padding: '8px',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px'
          }}>
            <ChefHat size={24} color="#6b7280" />
          </div>
          <h1 style={{
            fontSize: '20px',
            fontWeight: '800',
            color: 'rgb(31, 41, 55)',
            margin: '0px'
          }}>
            B.Tech Bawarchi
          </h1>
        </div>

        {/* Cart Button */}
        <button style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '44px',
          height: '44px',
          backgroundColor: 'rgb(243, 244, 246)',
          color: 'rgb(107, 114, 128)',
          border: 'none',
          borderRadius: '12px',
          cursor: 'pointer',
          transition: '0.2s',
          boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px',
          transform: 'scale(1)'
        }}>
          <ShoppingCart size={22} />
          {getCartItemCount() > 0 && (
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              backgroundColor: '#dc2626',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '700',
              boxShadow: '0 2px 10px rgba(220, 38, 38, 0.3)',
              animation: 'bounce 2s infinite'
            }}>
              {getCartItemCount()}
            </div>
          )}
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ padding: '0px 16px 16px' }}>
        <div style={{ position: 'relative' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: 'rgb(156, 163, 175)'
          }} />
          <input
            type="text"
            placeholder="Search for food..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '12px 12px 12px 44px',
              borderRadius: '12px',
              border: '2px solid rgb(229, 231, 235)',
              fontSize: '16px',
              width: '100%',
              outline: 'none',
              transition: 'border-color 0.2s',
              backgroundColor: 'white'
            }}
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div style={{ padding: '0px 16px 16px' }}>
        <div style={{
          display: 'flex',
          gap: '8px',
          overflowX: 'auto',
          paddingBottom: '4px',
          scrollbarWidth: 'none'
        }}>
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: 'none',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: '0.2s',
              backgroundColor: selectedCategory === 'all' ? 'rgb(255, 107, 53)' : 'rgb(243, 244, 246)',
              color: selectedCategory === 'all' ? 'white' : 'rgb(107, 114, 128)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <div style={{
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgb(243, 244, 246)'
            }}>
              <Grid3X3 size={16} color="#6b7280" />
            </div>
            All
          </button>
          {sortedCategories.map(category => {
            const IconComponent = getCategoryIcon(category);
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: '0.2s',
                  backgroundColor: selectedCategory === category ? 'rgb(255, 107, 53)' : 'rgb(243, 244, 246)',
                  color: selectedCategory === category ? 'white' : 'rgb(107, 114, 128)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <div style={{
                  padding: '6px',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgb(243, 244, 246)'
                }}>
                  <IconComponent size={16} color="#6b7280" />
                </div>
                {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Food Items Grid */}
      <div style={{ padding: '0px 16px' }}>
        {selectedCategory === 'all' ? (
          sortedCategories.map(category => (
            <div key={category} style={{ marginBottom: '24px' }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: 'rgb(31, 41, 55)',
                marginBottom: '16px',
                paddingLeft: '4px'
              }}>
                {category.charAt(0).toUpperCase() + category.slice(1).replace('-', ' ')}
              </h2>
              <div style={{
                display: 'grid',
                gap: '12px'
              }}>
                {groupedItems[category]?.map(item => (
                  <FoodItemCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={{
            display: 'grid',
            gap: '12px'
          }}>
            {filteredItems.map(item => (
              <FoodItemCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {/* Quantity Modal */}
      {showQuantityModal && selectedItem && (
        <QuantityModal
          item={selectedItem}
          isOpen={showQuantityModal}
          onClose={() => setShowQuantityModal(false)}
          onAddToCart={handleQuantityModalAddToCart}
        />
      )}


      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes bounce {
          0%, 100%, 20%, 50%, 80% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        /* Hide scrollbar for categories */
        div::-webkit-scrollbar {
          display: none;
        }
        
        /* Mobile-specific optimizations */
        @media (max-width: 480px) {
          .food-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
        }
        
        @media (min-width: 481px) and (max-width: 768px) {
          .food-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 12px;
          }
        }
      `}</style>
      
      {/* Show Order Status Bar if there's an active order, otherwise show cart */}
      {(() => {
        console.log('🔍 HomePageNew: Conditional rendering check', {
          currentOrder: !!currentOrder,
          orderId: currentOrder?.orderId,
          orderStatus: currentOrder?.status,
          cartItemCount: getCartItemCount(),
          currentOrderData: currentOrder
        });
        
        if (currentOrder) {
          console.log('🔍 HomePageNew: Rendering OrderStatusBar with order:', currentOrder);
          return <OrderStatusBar />;
        } else if (getCartItemCount() > 0) {
          console.log('🔍 HomePageNew: Rendering BottomCartBar with cart count:', getCartItemCount());
          return <BottomCartBar />;
        } else {
          console.log('🔍 HomePageNew: Rendering nothing - no order, no cart items');
          return null;
        }
      })()}

      {/* Sidebar */}
      {showSidebar && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'stretch'
        }}>
          {/* Sidebar Content */}
          <div style={{
            width: '280px',
            height: '100%',
            backgroundColor: 'white',
            boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Sidebar Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  backgroundColor: 'rgb(255, 107, 53)',
                  borderRadius: '8px',
                  padding: '8px'
                }}>
                  <ChefHat size={24} color="white" />
                </div>
                <div>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: 'rgb(31, 41, 55)',
                    margin: '0'
                  }}>
                    B.Tech Bawarchi
                  </h2>
                  <p style={{
                    fontSize: '12px',
                    color: 'rgb(107, 114, 128)',
                    margin: '0'
                  }}>
                    Quick Actions
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSidebar(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  backgroundColor: 'rgb(243, 244, 246)',
                  color: 'rgb(107, 114, 128)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Sidebar Menu Items */}
            <div style={{
              flex: 1,
              padding: '20px 0'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                {/* Profile */}
                <button
                  onClick={() => handleSidebarNavigation('/profile')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(249, 250, 251)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{
                    backgroundColor: 'rgb(239, 246, 255)',
                    borderRadius: '8px',
                    padding: '8px'
                  }}>
                    <User size={20} color="rgb(59, 130, 246)" />
                  </div>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'rgb(31, 41, 55)'
                    }}>
                      My Profile
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgb(107, 114, 128)'
                    }}>
                      Manage your account
                    </div>
                  </div>
                </button>

                {/* Order History */}
                <button
                  onClick={() => handleSidebarNavigation('/orders')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(249, 250, 251)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{
                    backgroundColor: 'rgb(254, 243, 199)',
                    borderRadius: '8px',
                    padding: '8px'
                  }}>
                    <History size={20} color="rgb(245, 158, 11)" />
                  </div>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'rgb(31, 41, 55)'
                    }}>
                      Order History
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgb(107, 114, 128)'
                    }}>
                      View past orders
                    </div>
                  </div>
                </button>

                {/* Saved Addresses */}
                <button
                  onClick={() => handleSidebarNavigation('/addresses')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(249, 250, 251)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <div style={{
                    backgroundColor: 'rgb(220, 252, 231)',
                    borderRadius: '8px',
                    padding: '8px'
                  }}>
                    <MapPin size={20} color="rgb(34, 197, 94)" />
                  </div>
                  <div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'rgb(31, 41, 55)'
                    }}>
                      Saved Addresses
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'rgb(107, 114, 128)'
                    }}>
                      Manage delivery addresses
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Sidebar Footer */}
            <div style={{
              padding: '20px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => {
                  setShowSidebar(false);
                  navigate('/');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: 'rgb(254, 242, 242)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(252, 231, 243)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(254, 242, 242)'}
              >
                <div style={{
                  backgroundColor: 'rgb(239, 68, 68)',
                  borderRadius: '6px',
                  padding: '6px'
                }}>
                  <LogOut size={16} color="white" />
                </div>
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'rgb(239, 68, 68)'
                  }}>
                    Back to Home
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Backdrop */}
          <div
            style={{
              flex: 1,
              height: '100%'
            }}
            onClick={() => setShowSidebar(false)}
          />
        </div>
      )}
    </div>
  );
};

export default HomePageNew;